import type { AuthAttribution } from '@viral-space/shared';

/**
 * Marketing-attribution capture for the game client.
 *
 * Visitors who arrive at the game (`game.playzerion.com/?utm_campaign=…`)
 * — usually after the marketing site forwards them with the same query
 * params — have their UTM bundle snapshotted into `localStorage` once.
 * The next signup forwards that snapshot in the `AuthRequest` so the
 * server can persist it on the new account row.
 *
 * Design choices:
 *   - **First-touch** wins: re-arriving with a different campaign in
 *     the URL does NOT overwrite an earlier capture. Multi-touch
 *     attribution is out of scope here; the marketing team only needs
 *     to know which campaign drove the original registration.
 *   - **Pre-signup only**: the snapshot is cleared on a successful
 *     signup so a second account from the same browser doesn't get
 *     stale tags. Login uses no attribution (nothing to update on the
 *     existing account).
 *   - **Best-effort storage**: every `localStorage` access is wrapped
 *     in try/catch — Safari private mode + iOS WKWebView in some
 *     contexts throw on storage writes. If we can't persist, we still
 *     pass the in-URL params through for the current session.
 */

const STORAGE_KEY = 'zerion:attribution';

/** Only these query keys are pulled into the attribution snapshot. */
const PARAM_TO_FIELD: Array<[string, keyof AuthAttribution]> = [
  ['utm_campaign', 'campaign'],
  ['utm_source', 'source'],
  ['utm_medium', 'medium'],
  ['utm_content', 'content'],
  ['utm_term', 'term'],
  ['gclid', 'gclid'],
  ['fbclid', 'fbclid'],
];

/**
 * Read the current URL's tracking params and return them in the wire
 * shape, or `null` if none of the recognised keys are present.
 */
export function readAttributionFromUrl(): AuthAttribution | null {
  if (typeof window === 'undefined') return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return null;
  }
  const out: AuthAttribution = {};
  let hasAny = false;
  for (const [key, field] of PARAM_TO_FIELD) {
    const v = params.get(key);
    if (!v) continue;
    const trimmed = v.trim();
    if (!trimmed) continue;
    // Cap at 256 chars to mirror the server-side trim guard so we
    // never ship a runaway adversarial value across the wire.
    out[field] = (trimmed.length > 256 ? trimmed.slice(0, 256) : trimmed) as never;
    hasAny = true;
  }
  if (!hasAny) return null;
  out.landingPath = window.location.pathname || '/';
  out.capturedAt = Date.now();
  return out;
}

/**
 * Persist `value` (or no-op if `value` is null). Idempotent against
 * pre-existing storage rows: the first capture wins, later URL hits
 * are dropped so we keep first-touch attribution.
 */
function writeStorage(value: AuthAttribution): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* private-mode or quota — best-effort */
  }
}

function readStorage(): AuthAttribution | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthAttribution | null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Capture attribution from the URL on bootstrap. Only writes if no
 * snapshot already exists (first-touch wins). Safe to call repeatedly.
 * Returns the live (cached or freshly-captured) snapshot, or `null`
 * if the visitor never had any tracking params attached.
 */
export function captureAttributionFromUrl(): AuthAttribution | null {
  if (typeof window === 'undefined') return null;
  const existing = readStorage();
  if (existing) return existing;
  const fresh = readAttributionFromUrl();
  if (!fresh) return null;
  writeStorage(fresh);
  return fresh;
}

/** Returns the persisted attribution snapshot, or null if none. */
export function getStoredAttribution(): AuthAttribution | null {
  if (typeof window === 'undefined') return null;
  return readStorage();
}

/**
 * Clear the persisted attribution. Called after a successful signup
 * so a second account created from the same browser doesn't inherit
 * the now-stale campaign tag.
 */
export function clearStoredAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* best-effort */
  }
}
